import type {Context} from '../types';
import {
  rootIdsWith,
  collectElementsWithType,
  hasFlattenedOverrides,
  reGuidOverridesArray,
  fixSymbolRootAndInstDataTypeJson,
} from '@packages/systems/core/render';
import {fixDataTypeJson} from '@packages/systems/core/component-migrations';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {Types, type ExpressionPath} from '@packages/systems/wfdl';
import {cloneDeep} from 'lodash';

const SYM_OVERRIDES_PATH: ExpressionPath = [
  {in: 'Record', at: 'sym'},
  {in: 'Record', at: 'overrides'},
];

const getOverrideFieldsNeedingFlattening = (
  jsonNode: JSONNode
): Array<string> | null => {
  const {data = {}, type = null} = jsonNode;
  const hasInstDataType = Boolean(type === 'Symbol' && data.sym.instDataType);
  const hasOverrides = Boolean(hasInstDataType && data.sym.overrides);

  if (!hasOverrides) return null;

  const overridesFieldsNeedingFlattening: Array<string> = [];

  const instDataType =
    typeof data.sym.instDataType === 'string'
      ? JSON.parse(data.sym.instDataType)
      : data.sym.instDataType;

  const overrides: {
    [key: string]: Array<JSONNode>;
  } = data.sym.overrides;

  const symOverridesType = Types.__DEPRECATED__getTypeAtExpressionPath(
    instDataType,
    SYM_OVERRIDES_PATH
  );

  const overrideFieldsTypes = symOverridesType
    ? Types.getRecordFields(symOverridesType)
    : null;

  if (!overrideFieldsTypes) return null;

  for (const overrideKey in overrides) {
    const overrideFieldType = overrideFieldsTypes[overrideKey];

    // Make sure we're working with a variation of List of Elements (i.g: RichTextChildren, List<Element>)
    if (
      !hasFlattenedOverrides(overrides[overrideKey]) &&
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      Types.isListOfElements(overrideFieldType)
    ) {
      overridesFieldsNeedingFlattening.push(overrideKey);
    }
  }

  return overridesFieldsNeedingFlattening;
};

const rootIds = rootIdsWith((node) => {
  const rawInstDataType = node.data?.sym?.instDataType;
  if (rawInstDataType) {
    const parsedInstDataType =
      // We store Types as strings in Mongo to work around nesting limit.
      typeof rawInstDataType === 'string'
        ? JSON.parse(rawInstDataType)
        : rawInstDataType;
    const newInstDataType = fixDataTypeJson(parsedInstDataType);

    return collectElementsWithType(node.data, newInstDataType);
  }
  return node.children;
});

/**
 * Takes a map of overrides and returns a tuple that includes a reference to
 * each JSONNode `_id` and an array of flattened nodes.
 * i.g: [flattenedOverridesRootIds, flattenedOverridesArray]
 */
export const flattenOverrides = (
  jsonNode: JSONNode,
  overridesFieldsNeedingFlattening: Array<string>,
  allJsonNodeAndOverrideIds: Set<string>
): {
  flattenedOverridesRootIds: {[key: string]: Array<string>};
  flattenedOverridesArray: Array<JSONNode>;
} =>
  overridesFieldsNeedingFlattening.reduce(
    (acc, overrideKey) => {
      const {data = {}} = jsonNode;

      /**
       * Checks the current jsonNode's overrides against our
       * `allJsonNodeAndOverrideIds` Set to see if we have duplicates, if so, we'll
       * assign new ids to the `overridesArray`.
       */
      const overridesArray = reGuidOverridesArray(
        data.sym.overrides[overrideKey],
        allJsonNodeAndOverrideIds
      );

      /**
       * Reassigns each override field's JSONNodes with references to their `_id`s.
       *
       * data.sym.overrides: {
       *   'myOverrideField1': [
       *     'childNodeReferenceId1',
       *     'childNodeReferenceId2',
       *   ],
       *   'myOverrideField2': [
       *     'childNodeReferenceId3',
       *     'childNodeReferenceId4',
       *   ],
       * }
       */
      acc.flattenedOverridesRootIds[overrideKey] = rootIds(overridesArray);

      /**
       * Flattens each override field into an array to insert into the JSONNode tree.
       */
      acc.flattenedOverridesArray =
        acc.flattenedOverridesArray.concat(overridesArray);

      return acc;
    },
    {flattenedOverridesRootIds: {}, flattenedOverridesArray: []} as {
      flattenedOverridesRootIds: {[key: string]: Array<string>};
      flattenedOverridesArray: Array<JSONNode>;
    }
  );

export default (context: Context) => {
  const {
    node: {jsonNode},
    tree: {nodeMap},
    fn: {insertNodesAfter},
  } = context;

  const {type, data = {}} = jsonNode;

  // We first run `fixDataTypeJson` because some sites have old `dataType`s
  // with incorrect shape and we need to make sure `dataTypes` are normalized so we
  // can flatten overrides properly. This is traditionally ran during reification
  // but needed to be lifted to a patch.
  const hasDataType = Boolean(jsonNode.dataType);
  const hasInstDataType = Boolean(type === 'Symbol' && data?.sym.instDataType);

  let jsonNodeShallowCopy = null;
  let isMigrationNeeded = false;

  if (hasDataType) {
    jsonNodeShallowCopy = {...jsonNode};

    fixSymbolRootAndInstDataTypeJson(jsonNodeShallowCopy);
  }

  if (hasInstDataType) {
    jsonNodeShallowCopy = {
      ...jsonNode,
      data: {
        ...data,
        sym: {
          ...data.sym,
        },
      },
    };

    fixSymbolRootAndInstDataTypeJson(jsonNodeShallowCopy);
  }

  // At the time of writing this migration, we have an issue where LineBreaks have
  // overrides. Don't migrate those.
  const isLineBreak = jsonNode.type === 'LineBreak';
  let overridesFieldsNeedingFlattening: Array<string> = [];

  if (!isLineBreak && jsonNodeShallowCopy) {
    overridesFieldsNeedingFlattening =
      getOverrideFieldsNeedingFlattening(jsonNodeShallowCopy) || [];

    isMigrationNeeded = Boolean(overridesFieldsNeedingFlattening.length);
  }

  return {
    needed: isMigrationNeeded,
    logData: {
      msg: 'Symbols should have flattened overrides',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      const allJsonNodeAndOverrideIds = new Set(nodeMap.keys());
      const jsonNodeCopy = cloneDeep(jsonNode);

      /**
       * If this JSONNode has overrides needing to be flattened, replace the
       * JSONNode's `data.sym.overrides` property with a flattened list of node ids
       * and append the nodes to the root tree.
       */
      const {flattenedOverridesRootIds, flattenedOverridesArray} =
        flattenOverrides(
          jsonNodeCopy,
          overridesFieldsNeedingFlattening,
          allJsonNodeAndOverrideIds
        );

      for (const override in flattenedOverridesRootIds) {
        data.sym.overrides[override] = flattenedOverridesRootIds[override];
      }

      insertNodesAfter(jsonNode._id, flattenedOverridesArray);
    },
  };
};
